/**
 * Path helpers for the shell navigation (plan 01 section 4.3, ported from AP
 * `lib/nav-paths.ts`).
 *
 * The rail, the mobile sheet, the header breadcrumb and the command palette
 * share these rules, so they always say the same thing. The nav items
 * themselves live in `lib/navigation.ts` (frozen by S0); this file only reads
 * them.
 */
import { ROOT_LABEL, activeHref, allNavItems, labelForPath, type NavItem } from './navigation'

/**
 * True when `item` is the single nav entry that lights for `pathname`
 * (longest match wins, so `/queries/billing/12` lights Billing Requests and
 * not Help Desk, and `/workbench` lights Explorer).
 */
export function isNavItemActive(
  pathname: string | null | undefined,
  item: Pick<NavItem, 'href'>,
  items: NavItem[] = allNavItems(),
): boolean {
  return activeHref(pathname, items) === item.href
}

export interface Crumb {
  label: string
  href: string
}

/**
 * Labels for path segments that are not a nav entry of their own, or whose
 * capitalised form reads wrong. Every other segment is title-cased with
 * hyphens turned into spaces.
 */
export const SEGMENT_LABELS: Readonly<Record<string, string>> = {
  ai: 'AI',
  'agent-hub': 'Agent Hub',
  'data-manager': 'Data Manager',
  receivables: 'Explorer',
  workbench: 'Explorer',
  invoices: 'Explorer',
  queries: 'Help Desk',
  'help-desk': 'Help Desk',
  billing: 'Billing Requests',
  'master-data': 'Master Data Requests',
  asset: 'Asset Requests',
  portal: 'Customer Portal',
  'cash-application': 'Cash Application',
  credit: 'Credit',
  customer: 'Customer',
}

/**
 * Accumulated paths that have no page of their own. They are left out of the
 * trail, so every crumb can be clicked: `/ai/policies` reads
 * "Command Briefing / AI Policies", not "... / AI / AI Policies".
 */
const PATHS_WITHOUT_PAGE = new Set<string>(['/ai', '/admin', '/credit/customer'])

/**
 * Paths that render inside another nav entry. The crumb names and links to
 * that entry instead: the Workbench is opened from the Explorer, so
 * `/workbench` reads "Command Briefing / Explorer / INV-310001".
 */
const PATH_ALIASES: Readonly<Record<string, Crumb>> = {
  '/workbench': { label: 'Explorer', href: '/receivables' },
  '/invoices': { label: 'Explorer', href: '/receivables' },
  '/portal/tickets': { label: 'Help Desk', href: '/queries' },
}

const ID_SEGMENT = /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+)$/i

export function segmentLabel(segment: string): string {
  if (SEGMENT_LABELS[segment]) return SEGMENT_LABELS[segment]
  // A database id is never shown raw; the page names it with `setCrumbLabel`.
  if (ID_SEGMENT.test(segment)) return 'Details'
  const text = decodeURIComponent(segment).replace(/-/g, ' ')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/**
 * The header breadcrumb: the root crumb first, then one crumb per accumulated
 * path. A crumb takes, in order: the label a page set with `setCrumbLabel`,
 * the nav label for its exact href, then `segmentLabel`.
 */
export function breadcrumbTrail(
  pathname: string | null | undefined,
  labelFor: (href: string) => string | undefined,
  rootLabel: string,
): Crumb[] {
  const trail: Crumb[] = [{ label: labelFor('/') ?? rootLabel, href: '/' }]
  let current = ''
  for (const segment of (pathname ?? '/').split('/').filter(Boolean)) {
    current += `/${segment}`
    const alias = PATH_ALIASES[current]
    if (alias) {
      if (trail[trail.length - 1]?.href !== alias.href) trail.push(alias)
      // A page that names itself on an aliased path ("/workbench" naming the
      // open invoice) adds one crumb after the alias.
      const own = crumbLabelFor(current)
      if (own) trail.push({ label: own, href: current })
      continue
    }
    if (PATHS_WITHOUT_PAGE.has(current)) continue
    // A path that is its own rail entry starts a fresh trail under the root:
    // Billing Requests sits under Requests in the rail, not under Help Desk,
    // so `/queries/billing/12` reads "Command Briefing / Billing Requests / BIL-300007".
    const navLabel = labelFor(current)
    if (navLabel && trail.length > 1) trail.splice(1)
    trail.push({
      label: crumbLabelFor(current) ?? navLabel ?? segmentLabel(segment),
      href: current,
    })
  }
  return trail
}

/** The breadcrumb for `pathname`, from the AR nav config. */
export function breadcrumbsFor(pathname: string | null | undefined): Crumb[] {
  return breadcrumbTrail(pathname, labelForPath, ROOT_LABEL)
}

// Page-provided crumb labels
//
// A detail page whose path ends in a database id ("/queries/billing/12") or
// that lives on an aliased path ("/workbench") names its own crumb
// ("BIL-300011", "INV-310001") once it has loaded. The header subscribes, so
// the label appears without a navigation. Pass `null` to clear on unmount.

const crumbOverrides = new Map<string, string>()
const crumbListeners = new Set<() => void>()
let crumbVersion = 0

/** Set (or with `null` clear) the crumb label for one exact href. */
export function setCrumbLabel(href: string, label: string | null): void {
  const text = label?.trim() || null
  if ((crumbOverrides.get(href) ?? null) === text) return
  if (text) crumbOverrides.set(href, text)
  else crumbOverrides.delete(href)
  crumbVersion += 1
  crumbListeners.forEach((listener) => listener())
}

export function crumbLabelFor(href: string): string | undefined {
  return crumbOverrides.get(href)
}

export function subscribeCrumbLabels(listener: () => void): () => void {
  crumbListeners.add(listener)
  return () => {
    crumbListeners.delete(listener)
  }
}

export function crumbLabelsVersion(): number {
  return crumbVersion
}

// Browser tab title

/** Product name after the page name in the browser tab. */
export const APP_TITLE = 'AR Command Center'

/**
 * The browser tab title for `pathname`, from the same trail as the header:
 * "Command Briefing | AR Command Center", "BIL-300007 | AR Command Center".
 * The Workbench reads "Invoice INV-310001 | AI Workbench" once it has named
 * its crumb.
 */
export function documentTitleFor(pathname: string | null | undefined): string {
  const path = pathname ?? '/'
  if (path === '/workbench' || path.startsWith('/workbench/')) {
    const invoice = crumbLabelFor('/workbench')
    return invoice ? `Invoice ${invoice} | AI Workbench` : `AI Workbench | ${APP_TITLE}`
  }
  const trail = breadcrumbsFor(path)
  return `${trail[trail.length - 1].label} | ${APP_TITLE}`
}
